import { Component, OnInit, QueryList, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { CommonModule, PlatformLocation  } from '@angular/common';
import { WidgetRendererComponent } from '../widget-renderer/widget-renderer.component';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { NavigationEnd } from '@angular/router';
import { GlobalFilterComponent, GlobalFilterV2Component } from '@custom';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { PlatformService } from '@shared-lib/shared-services/platform.service.service';
import { RoleVisibilityService } from '../shared/role-visibility/role-visibility.service';

@Component({
  selector: 'app-dashboard',
  imports: [
    HttpClientModule,
    WidgetRendererComponent,
    CommonModule,
    GlobalFilterComponent,
    GlobalFilterV2Component,
  ],
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  animations: [
    trigger('fadeIn', [
      transition('* => *', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
})
export class DashboardComponent implements OnInit {
  private readonly mapDebugTag = '[REP-MAP-DBG]';
  configurations: any = {};
  useV2Filter: boolean = false;
  dashboardType: any;
  @ViewChildren('chartContainer', { read: ViewContainerRef })
  chartContainers!: QueryList<ViewContainerRef>;
  private subscriptions = new Subscription();

  globalDropdownConfig: any;
  selectedYear: number = 2024;
  configFileName!: string;
  private lastLoadedMine = '';  // Track the mine from last load
  private lastLoadedEngine = '';  // Track the engine from last load

  constructor(
    public configService: ConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private globalFilterService: GlobalFilterService,
    private platformService: PlatformService,
    private roleVisibilityService: RoleVisibilityService
  ) {}

  ngOnInit() {
    this.subscribeToRouterEvents();
    const data = this.route.snapshot.data;
    if (data['configFile']) {
      this.configFileName = data['configFile'];
      this.loadCharts();
    } else {
      const projectId = environment.PROJECT_ID;
      const appId = this.route.snapshot.data['dvmWorkflowAppId'];
      if (appId) {
        this.loadDashboardFromApi(projectId, appId);
      }
    }
    this.globalFilterService.updateAllFilters({
      queryParams: {},
      requestParams: {},
      widgetId: null,
    });
    this.globalFilterService.activeFilter = null;

    this.globalFilterService.resetFilterSelection('customer_spec');
  }

  private subscribeToRouterEvents() {
    const navSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Re-check config file in case route data changed
      const data = this.route.snapshot.data;
      if (data['configFile'] && data['configFile'] !== this.configFileName) {
        this.configFileName = data['configFile'];
        this.loadCharts();
      } else if (data['configFile']) {
        // Same URL navigation is configured to reload globally.
        // Only rebuild the dashboard when there is meaningful navigation state to apply.
        if (this.shouldReloadCurrentConfig()) {
          this.loadCharts();
        }
      }
    });
    this.subscriptions.add(navSub);
  }

  loadDashboardFromApi(projectId: string, appId: string) {
    const configSub = forkJoin({
      response: this.platformService.getDashboardConfigFromApi(projectId, appId),
      visibility: this.roleVisibilityService.loadMapping(),
    })
    .subscribe({
      next: ({ response }) => {
        if (response?.length) {
          const dashboardConfig = JSON.parse(JSON.stringify(response[0].dvm_workflow_map));
          const state = history.state;

          if (this.shouldApplyNavigationState(state)) {
            this.injectStateIntoConfig(dashboardConfig, state);
          }

          this.configurations = this.roleVisibilityService.applyToDashboardConfig(dashboardConfig);
          this.useV2Filter = this.checkUseV2(this.configurations);
        }
      },
      error: (err) => console.error('Error loading dashboard config:', err),
    });
    this.subscriptions.add(configSub);
  }

  loadCharts() {
    const state = history.state;
    const currentMine = state?.['selectedMineName'] || 
                       state?.['selectedMine']?.name || 
                       state?.['selectedMine']?.__mineRaw ||
                       '';
    const currentEngine = state?.['engine_model'] || 
                         state?.['selectedEngineModelKey'] ||
                         state?.['selectedEngine'] || '';
    
    if (currentMine) {
      this.lastLoadedMine = currentMine;
    }
    if (currentEngine) {
      this.lastLoadedEngine = currentEngine;
    }

    const configSub = forkJoin({
      response: this.configService.getDashboardConfig(this.configFileName),
      visibility: this.roleVisibilityService.loadMapping(),
    })
      .subscribe(({ response }) => {
        let dashboardConfig = response;

        console.log(`${this.mapDebugTag} Dashboard.loadCharts received dashboard config.`, {
          configFileName: this.configFileName,
          hasNavigationState: this.shouldApplyNavigationState(state),
          selectedRegion: state?.['selectedRegion'] || null,
          selectedMineName:
            state?.['selectedMineName'] ||
            state?.['selectedMine']?.name ||
            state?.['selectedMine']?.__mineRaw ||
            null,
          selectedEngine:
            state?.['engine_model'] ||
            state?.['selectedEngineModelKey'] ||
            state?.['selectedEngine'] ||
            null,
        });
        
        if (this.shouldApplyNavigationState(state)) {
          // Deep clone to ensure reference changes for components' ngOnChanges
          dashboardConfig = JSON.parse(JSON.stringify(response));
          this.injectStateIntoConfig(dashboardConfig, state);
        }
        
        this.configurations = this.roleVisibilityService.applyToDashboardConfig(dashboardConfig);
        this.useV2Filter = this.checkUseV2(this.configurations);
      });
    this.subscriptions.add(configSub);
  }

  private hasContext(state: any): boolean {
    return !!(
      state?.['selectedMine'] ||
      state?.['selectedMineName'] ||
      state?.['selectedRegion'] ||
      state?.['regionColors'] ||
      state?.['selectedEngineModelKey'] ||
      state?.['engine_model_key'] ||
      state?.['engine_model'] ||
      state?.['selectedEngine'] ||
      state?.['selectedEngineWithoutEmission'] ||
      state?.['engine_model_without_emission']
    );
  }

  private shouldApplyNavigationState(state: any): boolean {
    if (!this.hasContext(state)) {
      return false;
    }

    const configPath = (this.configFileName || '').toLowerCase();

    // Screen 1 should always load from its base JSON/filter state.
    // Reusing browser navigation state here can leave the map stuck in
    // a stale drill-down context when the user reopens the same screen.
    if (configPath.includes('repower-predictor/repower-predictor.json')) {
      return false;
    }

    return true;
  }

  private shouldReloadCurrentConfig(): boolean {
    const state = this.shouldApplyNavigationState(history.state);
    if (!state) {
      return false;
    }

    // Check if MINE has changed
    const currentMine = history.state?.['selectedMineName'] || 
                       history.state?.['selectedMine']?.name || 
                       history.state?.['selectedMine']?.__mineRaw ||
                       '';
    
    const hasMineChanged = currentMine && currentMine !== this.lastLoadedMine;
    
    // Check if ENGINE has changed
    const currentEngine = history.state?.['engine_model'] || 
                         history.state?.['selectedEngineModelKey'] ||
                         history.state?.['selectedEngine'] || '';
    
    const hasEngineChanged = currentEngine && currentEngine !== this.lastLoadedEngine;

    // Reload if either mine or engine changed
    if (hasMineChanged || hasEngineChanged) {
      this.lastLoadedMine = currentMine;
      this.lastLoadedEngine = currentEngine;
      return true;
    }

    return false;
  }

  private checkUseV2(config: any): boolean {
    if (!config) return false;
    if (this.configFileName && this.configFileName.includes('screen-2')) return true;
    const listener = config?.filters?.filterListener;
    if (listener && listener.toString().toLowerCase().includes('world-map-2')) return true;
    return false;
  }

  private injectStateIntoConfig(config: any, state: any) {
    const mineContext = this.extractSelectedMineContext(state);
    const selectedMineActive = this.getSelectedMineActiveValue(state);

    console.log(`${this.mapDebugTag} Dashboard.injectStateIntoConfig applying state.`, {
      configFileName: this.configFileName,
      mine: mineContext.mine,
      region: mineContext.region,
      engineModelKey: mineContext.engineModelKey,
      selectedMineActive,
    });

    // Update global filter service with region so it gets passed to all listening charts
    const regionToUse = mineContext.region || state['selectedRegion'];
    if (regionToUse) {
      this.globalFilterService.updateFilterSelection('region', regionToUse, false);
    }

    // Inject into global filters
    if (config?.filters && state['selectedRegion']) {
      const filters = Array.isArray(config.filters) ? config.filters : [config.filters];
      for (const filter of filters) {
        if (filter.customPayload?.requestData?.filter) {
          filter.customPayload.requestData.filter.region = state['selectedRegion'];
        } else if (filter.customPayload?.requestData) {
          filter.customPayload.requestData.region = state['selectedRegion'];
        }
      }
    }

    if (config?.pageContent) {
      for (const row of config.pageContent) {
        if (row.columns) {
          for (const col of row.columns) {
            if (col.type === 'world-map') {
              if (state['selectedMine']) col['selectedMine'] = state['selectedMine'];
              if (state['selectedRegion']) col['selectedRegion'] = state['selectedRegion'];
              if (state['regionColors'] && typeof state['regionColors'] === 'object') {
                col['regionColors'] = { ...state['regionColors'] };
              }
            }

            // Inject selected mine into tree-drill card payload
            if (mineContext.mine && col.type === 'tree-drill-down') {
              if (!col.customPayload) col.customPayload = {};
              col.customPayload.mine = mineContext.mine;
              // Also inject country and region for API filtering
              if (state['selectedMine']?.__countryRaw) {
                col.customPayload.country = state['selectedMine'].__countryRaw;
              }
              if (state['selectedMine']?.__regionRaw) {
                col.customPayload.region = state['selectedMine'].__regionRaw;
              }
              if (selectedMineActive) {
                col.customPayload.active = selectedMineActive;
              }
            }

            // Inject selected mine context into widget request filters
            if (col.customPayload?.requestData?.filter) {
              const isFleet = this.isFleetLifeStage(col);
              const isEngineDowntime = this.isEngineDowntimeWidget(col);
              if (!isFleet && mineContext.mine) {
                col.customPayload.requestData.filter.mine = mineContext.mine;
              }
              // Engine downtime payload should not send customer.
              if (isEngineDowntime) {
                delete col.customPayload.requestData.filter.customer;
                if (mineContext.mine) {
                  col.customPayload.requestData.filter.site = mineContext.mine;
                }
                if (state['selectedMine']?.__countryRaw) {
                  col.customPayload.requestData.filter.country = state['selectedMine'].__countryRaw;
                }
                const cleanEquipmentModel =
                  state?.['selectedMine']?.__fullRow?.clean_equipment_model ||
                  state?.['selectedMine']?.__fullRow?.truck_model ||
                  state?.['selectedTruckModel'] ||
                  state?.['truck_model'];
                if (cleanEquipmentModel) {
                  col.customPayload.requestData.filter.clean_equipment_model = cleanEquipmentModel;
                }
                delete col.customPayload.requestData.filter.mine;
                delete col.customPayload.requestData.filter.mine_site;
              } else if (mineContext.customer && mineContext.customer.trim()) {
                col.customPayload.requestData.filter.customer = mineContext.customer;
              } else if ('customer' in col.customPayload.requestData.filter) {
                delete col.customPayload.requestData.filter.customer;
              }
              if (!isFleet && mineContext.engineModelKey) {
                col.customPayload.requestData.filter.engine_model_key = mineContext.engineModelKey;
              }
              if (!isFleet && mineContext.region) {
                col.customPayload.requestData.filter.region = mineContext.region;
              }
              if (isFleet) {
                // FleetLifeStageService wants mine_site + engine_model + machine_model
                if (mineContext.mine) col.customPayload.requestData.filter.mine_site = mineContext.mine;
                if (mineContext.engineModelKey) col.customPayload.requestData.filter.engine_model = mineContext.engineModelKey;
                if (mineContext.machineModel) col.customPayload.requestData.filter.machine_model = mineContext.machineModel;
                delete col.customPayload.requestData.filter.mine;
                delete col.customPayload.requestData.filter.engine_model_key;
              }
            }

            if (col.customPayload?.requestData && !col.customPayload?.requestData?.filter) {
              const isFleet = this.isFleetLifeStage(col);
              const isEngineDowntime = this.isEngineDowntimeWidget(col);
              const isFailureDetails = this.isFailureDetailsWidget(col);
              
              if (!isFleet && !isFailureDetails && mineContext.mine) {
                col.customPayload.requestData.mine = mineContext.mine;
              }
              if (!isFleet && !isFailureDetails && mineContext.engineModelKey) {
                col.customPayload.requestData.engine_model_key = mineContext.engineModelKey;
              }
              if (!isFleet && !isFailureDetails && mineContext.region) {
                col.customPayload.requestData.region = mineContext.region;
              }
              if (isFleet) {
                // FleetLifeStageService wants mine_site + engine_model + machine_model
                if (mineContext.mine) col.customPayload.requestData.mine_site = mineContext.mine;
                if (mineContext.engineModelKey) col.customPayload.requestData.engine_model = mineContext.engineModelKey;
                if (mineContext.machineModel) col.customPayload.requestData.machine_model = mineContext.machineModel;
                delete col.customPayload.requestData.mine;
                delete col.customPayload.requestData.engine_model_key;
              }
              if (isFailureDetails) {
                 if (mineContext.mine) col.customPayload.requestData.mine_site = mineContext.mine;
                 if (mineContext.engineModelKey) col.customPayload.requestData.engine_platform = mineContext.engineModelKey.split(',')[0].trim();
                 delete col.customPayload.requestData.mine;
                 delete col.customPayload.requestData.engine_model_key;
                 delete col.customPayload.requestData.engine_model;
                 delete col.customPayload.requestData.region;
              }
              if (isEngineDowntime) {
                if (mineContext.mine) col.customPayload.requestData.site = mineContext.mine;
                if (state['selectedMine']?.__countryRaw) {
                  col.customPayload.requestData.country = state['selectedMine'].__countryRaw;
                }
                const cleanEquipmentModel =
                  state?.['selectedMine']?.__fullRow?.clean_equipment_model ||
                  state?.['selectedMine']?.__fullRow?.truck_model ||
                  state?.['selectedTruckModel'] ||
                  state?.['truck_model'];
                if (cleanEquipmentModel) {
                  col.customPayload.requestData.clean_equipment_model = cleanEquipmentModel;
                }
                delete col.customPayload.requestData.mine;
                delete col.customPayload.requestData.mine_site;
              }
            }

            // Inject selected mine into tree-drill card payload
            if (mineContext.mine && col.type === 'tree-drill-down') {
              if (!col.customPayload) col.customPayload = {};
              col.customPayload.mine = mineContext.mine;
              if (selectedMineActive) {
                col.customPayload.active = selectedMineActive;
              }
            }

            // Inject selected mine into donut-chart payload
            if (mineContext.mine && col.type === 'donut-chart') {
              if (!col.customPayload) col.customPayload = {};
              if (!col.customPayload.requestData) col.customPayload.requestData = {};
              col.customPayload.requestData.mine = mineContext.mine;
            }

            // Inject selected mine context into widget request filters (second pass)
            if (col.customPayload?.requestData?.filter) {
              const isFleet2 = this.isFleetLifeStage(col);
              const isEngineDowntime2 = this.isEngineDowntimeWidget(col);
              if (!isFleet2 && mineContext.mine) {
                col.customPayload.requestData.filter.mine = mineContext.mine;
              }
              // Engine downtime payload should not send customer.
              if (isEngineDowntime2) {
                delete col.customPayload.requestData.filter.customer;
                if (mineContext.mine) {
                  col.customPayload.requestData.filter.site = mineContext.mine;
                }
                if (state['selectedMine']?.__countryRaw) {
                  col.customPayload.requestData.filter.country = state['selectedMine'].__countryRaw;
                }
                const cleanEquipmentModel =
                  state?.['selectedMine']?.__fullRow?.clean_equipment_model ||
                  state?.['selectedMine']?.__fullRow?.truck_model ||
                  state?.['selectedTruckModel'] ||
                  state?.['truck_model'];
                if (cleanEquipmentModel) {
                  col.customPayload.requestData.filter.clean_equipment_model = cleanEquipmentModel;
                }
                delete col.customPayload.requestData.filter.mine;
                delete col.customPayload.requestData.filter.mine_site;
              } else if (mineContext.customer && mineContext.customer.trim()) {
                col.customPayload.requestData.filter.customer = mineContext.customer;
              } else if ('customer' in col.customPayload.requestData.filter) {
                delete col.customPayload.requestData.filter.customer;
              }
              if (!isFleet2 && mineContext.engineModelKey) {
                col.customPayload.requestData.filter.engine_model_key = mineContext.engineModelKey;
              }
              if (isFleet2) {
                if (mineContext.mine) col.customPayload.requestData.filter.mine_site = mineContext.mine;
                if (mineContext.engineModelKey) col.customPayload.requestData.filter.engine_model = mineContext.engineModelKey;
                delete col.customPayload.requestData.filter.mine;
                delete col.customPayload.requestData.filter.engine_model_key;
              }
            }

            
            // Inject region into customPayload for all widgets
            if (state['selectedRegion'] && col.customPayload?.requestData) {
              const skipRegion = this.isFailureDetailsWidget(col);
              if (!skipRegion) {
                if (col.customPayload.requestData.filter) {
                  col.customPayload.requestData.filter.region = state['selectedRegion'];
                } else {
                  col.customPayload.requestData.region = state['selectedRegion'];
                }
              }
            }

            // Apply the same state context to nested widgets (e.g. TCO comparison cards inside rows).
            if (Array.isArray(col?.rows) && col.rows.length > 0) {
              this.applyStateToNestedRows(col.rows, mineContext, state);
            }
          }
        }
      }
    }
  }

  private extractSelectedMineContext(state: any): { mine: string; customer: string; engineModelKey: string; region: string; machineModel: string } {
    const selectedMine = state?.['selectedMine'];
    const mine =
      state?.['selectedMineName'] ||
      selectedMine?.name ||
      selectedMine?.mine ||
      selectedMine?.__mineRaw ||
      (typeof selectedMine === 'string' ? selectedMine : '');

    const customer = state?.['selectedCustomer'] || '';
    const engineModelKey =
      state?.['selectedEngineModelKey'] ||
      state?.['engine_model_key'] ||
      state?.['engine_model'] ||
      state?.['selectedEngine'] ||
      state?.['selectedEngineWithoutEmission'] ||
      state?.['engine_model_without_emission'] ||
      '';

    const region = state?.['selectedRegion'] || 
                   selectedMine?.__regionRaw || 
                   selectedMine?.region || 
                   '';

    const machineModel =
      state?.['selectedTruckModel'] ||
      state?.['truck_model'] ||
      state?.['machine_model'] ||
      state?.['clean_equipment_model'] ||
      state?.['selectedMine']?.__fullRow?.clean_equipment_model ||
      state?.['selectedMine']?.__fullRow?.truck_model ||
      '';

    return {
      mine: mine || '',
      customer,
      engineModelKey,
      region,
      machineModel: machineModel || '',
    };
  }

  private getSelectedMineActiveValue(state: any): string {
    const selectedMine = state?.['selectedMine'];
    if (!selectedMine || typeof selectedMine !== 'object') {
      return '';
    }

    if (selectedMine.__isActiveRepower === true) {
      return 'yes';
    }

    return 'no';
  }

  private addSelectedMineToWorldMap(selectedMine: any) {
    this.injectStateIntoConfig(this.configurations, { selectedMine });
  }

  private applyStateToNestedRows(rows: any[], mineContext: { mine: string; customer: string; engineModelKey: string; region: string }, state: any): void {
    for (const row of rows) {
      if (row?.type) {
        this.applyStateToWidget(row, mineContext, state);
      }

      if (Array.isArray(row?.columns)) {
        for (const col of row.columns) {
          this.applyStateToWidget(col, mineContext, state);
          if (Array.isArray(col?.rows) && col.rows.length > 0) {
            this.applyStateToNestedRows(col.rows, mineContext, state);
          }
        }
      }
    }
  }

  private isFleetLifeStage(col: any): boolean {
    const fn = col?.customPayload?.endpoints?.functionName ||
               col?.endpoints?.functionName ||
               col?.functionName || '';
    return fn === 'FleetLifeStageService';
  }

  private isEngineDowntimeWidget(col: any): boolean {
    const fn = col?.customPayload?.endpoints?.functionName ||
               col?.endpoints?.functionName ||
               col?.functionName || '';
    return fn === 'FetchEngineDowntime';
  }

  private isFailureDetailsWidget(col: any): boolean {
    const fn = col?.customPayload?.endpoints?.functionName ||
               (Array.isArray(col?.customPayload?.endpoints) && col?.customPayload?.endpoints[0]?.functionName) ||
               col?.endpoints?.functionName ||
               (Array.isArray(col?.endpoints) && col?.endpoints[0]?.functionName) ||
               col?.functionName || '';
    return fn === 'FailureDetails';
  }

  private formatFleetEngineModel(value: string): string {
    const cleaned = `${value || ''}`
      .replace(/,/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^%+|%+$/g, '');

    return cleaned ? `%${cleaned}%` : '';
  }

  private applyStateToWidget(col: any, mineContext: { mine: string; customer: string; engineModelKey: string; region: string }, state: any): void {
    if (!col) return;

    const selectedMineActive = this.getSelectedMineActiveValue(state);
    if (mineContext.mine && col.type === 'tree-drill-down') {
      if (!col.customPayload) col.customPayload = {};
      col.customPayload.mine = mineContext.mine;
      if (state['selectedMine']?.__countryRaw) {
        col.customPayload.country = state['selectedMine'].__countryRaw;
      }
      if (state['selectedMine']?.__regionRaw) {
        col.customPayload.region = state['selectedMine'].__regionRaw;
      }
      if (selectedMineActive) {
        col.customPayload.active = selectedMineActive;
      }
    }

    if (!col.customPayload?.requestData) return;

    const isFleet = this.isFleetLifeStage(col);
    const isEngineDowntime = this.isEngineDowntimeWidget(col);
    const isFailureDetails = this.isFailureDetailsWidget(col);

    if (col.customPayload.requestData.filter) {
      if (!isFleet && !isFailureDetails && mineContext.mine) {
        col.customPayload.requestData.filter.mine = mineContext.mine;
      }
      if (isEngineDowntime) {
        delete col.customPayload.requestData.filter.customer;
        if (mineContext.mine) col.customPayload.requestData.filter.site = mineContext.mine;
        if (state['selectedMine']?.__countryRaw) {
          col.customPayload.requestData.filter.country = state['selectedMine'].__countryRaw;
        }
        const cleanEquipmentModel =
          state?.['selectedMine']?.__fullRow?.clean_equipment_model ||
          state?.['selectedMine']?.__fullRow?.truck_model ||
          state?.['selectedTruckModel'] ||
          state?.['truck_model'];
        if (cleanEquipmentModel) {
          col.customPayload.requestData.filter.clean_equipment_model = cleanEquipmentModel;
        }
        delete col.customPayload.requestData.filter.mine;
        delete col.customPayload.requestData.filter.mine_site;
      } else if (mineContext.customer && mineContext.customer.trim()) {
        col.customPayload.requestData.filter.customer = mineContext.customer;
      } else if ('customer' in col.customPayload.requestData.filter) {
        delete col.customPayload.requestData.filter.customer;
      }
      if (!isFleet && !isFailureDetails && mineContext.engineModelKey) {
        col.customPayload.requestData.filter.engine_model_key = mineContext.engineModelKey;
      }
      if (isFleet) {
        if (mineContext.mine) col.customPayload.requestData.filter.mine_site = mineContext.mine;
        if (mineContext.engineModelKey) {
          col.customPayload.requestData.filter.engine_model = this.formatFleetEngineModel(mineContext.engineModelKey);
        }
        delete col.customPayload.requestData.filter.mine;
        delete col.customPayload.requestData.filter.engine_model_key;
      }
      if (isFailureDetails) {
        if (mineContext.mine) col.customPayload.requestData.filter.mine_site = mineContext.mine;
        if (mineContext.engineModelKey) {
          col.customPayload.requestData.filter.engine_platform = mineContext.engineModelKey.split(',')[0].trim();
        }
        delete col.customPayload.requestData.filter.mine;
        delete col.customPayload.requestData.filter.engine_model_key;
        delete col.customPayload.requestData.engine_model;
        delete col.customPayload.requestData.filter.region;
      }
      if (!isFailureDetails && (state['selectedRegion'] || mineContext.region)) {
        col.customPayload.requestData.filter.region = mineContext.region || state['selectedRegion'];
      }
      return;
    }

    if (!isFleet && !isFailureDetails && mineContext.mine) {
      col.customPayload.requestData.mine = mineContext.mine;
    }
    if (!isFleet && !isFailureDetails && mineContext.engineModelKey) {
      col.customPayload.requestData.engine_model_key = mineContext.engineModelKey;
    }
    if (isFleet) {
      if (mineContext.mine) col.customPayload.requestData.mine_site = mineContext.mine;
      if (mineContext.engineModelKey) {
        col.customPayload.requestData.engine_model = this.formatFleetEngineModel(mineContext.engineModelKey);
      }
      delete col.customPayload.requestData.mine;
      delete col.customPayload.requestData.engine_model_key;
    }
    if (isFailureDetails) {
      if (mineContext.mine) col.customPayload.requestData.mine_site = mineContext.mine;
      if (mineContext.engineModelKey) {
        col.customPayload.requestData.engine_platform = mineContext.engineModelKey.split(',')[0].trim();
      }
      delete col.customPayload.requestData.mine;
      delete col.customPayload.requestData.engine_model_key;
      delete col.customPayload.requestData.engine_model;
      delete col.customPayload.requestData.region;
    }
    if (isEngineDowntime) {
      if (mineContext.mine) col.customPayload.requestData.site = mineContext.mine;
      if (state['selectedMine']?.__countryRaw) {
        col.customPayload.requestData.country = state['selectedMine'].__countryRaw;
      }
      const cleanEquipmentModel =
        state?.['selectedMine']?.__fullRow?.clean_equipment_model ||
        state?.['selectedMine']?.__fullRow?.truck_model ||
        state?.['selectedTruckModel'] ||
        state?.['truck_model'];
      if (cleanEquipmentModel) {
        col.customPayload.requestData.clean_equipment_model = cleanEquipmentModel;
      }
      delete col.customPayload.requestData.mine;
      delete col.customPayload.requestData.mine_site;
    }
    if (!isFailureDetails && (state['selectedRegion'] || mineContext.region)) {
      col.customPayload.requestData.region = mineContext.region || state['selectedRegion'];
    }
  }
}
